/**
 * Custom hook for backend API calls with loading and error states
 */
import { useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';

/**
 * Hook for fetching data from backend
 * @param {function} apiFunction - The API function to call
 * @param {array} dependencies - Dependencies array for useEffect
 */
export const useApi = (apiFunction, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiFunction();
      setData(result);
    } catch (err) {
      setError(err);
      console.error('API Hook Error:', err);
    } finally {
      setLoading(false);
    }
  }, [apiFunction]);

  useEffect(() => {
    fetchData();
  }, dependencies);

  const refetch = useCallback(fetchData, [fetchData]);

  return { data, loading, error, refetch };
};

/**
 * Hook for backend health check
 */
export const useBackendHealth = () => {
  const [health, setHealth] = useState(null);
  const [isHealthy, setIsHealthy] = useState(false);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const result = await api.checkBackendHealth();
        setHealth(result);
        setIsHealthy(result.status === 'healthy');
      } catch (err) {
        console.error('Health check error:', err);
        setIsHealthy(false);
      }
    };

    checkHealth();
    // Check health every 30 seconds
    const interval = setInterval(checkHealth, 30000);

    return () => clearInterval(interval);
  }, []);

  return { health, isHealthy };
};

export default {
  useApi,
  useBackendHealth,
};
